import { useState, useMemo } from "react";
import axios from "axios";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { Loader2, CreditCard, ShieldCheck } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const FUNDING_LABEL = { credit: "Credit", debit: "Debit", prepaid: "Prepaid", unknown: "Unknown" };

const cardElementOptions = {
  style: {
    base: { color: "#fff", fontSize: "15px", "::placeholder": { color: "rgba(255,255,255,0.35)" } },
    invalid: { color: "#f87171" },
  },
};

function Inner({ bookingId, balanceDue, onPaid }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [quote, setQuote] = useState(null);
  const [funding, setFunding] = useState(null);
  const [paymentMethodId, setPaymentMethodId] = useState(null);
  const [error, setError] = useState("");
  const requestKey = useMemo(() => crypto.randomUUID(), [bookingId]);

  const inspectCard = async () => {
    if (!stripe || !elements) return;
    setError(""); setBusy(true);
    try {
      const card = elements.getElement(CardElement);
      const { error: pmError, paymentMethod } = await stripe.createPaymentMethod({ type: "card", card });
      if (pmError) { setError(pmError.message || "Card details are invalid."); setBusy(false); return; }
      const detectedFunding = paymentMethod.card?.funding || "unknown";
      setFunding(detectedFunding);
      setPaymentMethodId(paymentMethod.id);
      const { data } = await axios.get(`${API}/payments/fee-preview`, { params: { subtotal: balanceDue, funding: detectedFunding } });
      setQuote(data);
    } catch {
      setError("Could not verify your card. Please try again.");
    } finally { setBusy(false); }
  };

  const confirmPayment = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await axios.post(`${API}/payments/charge-direct`, {
        booking_id: bookingId, payment_method_id: paymentMethodId, request_key: requestKey,
      });
      if (data.status === "succeeded") { onPaid(data); return; }
      if (data.status === "requires_action") {
        const { error: confirmError } = await stripe.confirmCardPayment(data.client_secret);
        if (confirmError) { setError(confirmError.message || "Payment could not be authenticated."); setBusy(false); return; }
        const poll = async (attempt = 0) => {
          if (attempt >= 8) { setError("Still confirming — if you completed authentication, refresh in a moment."); setBusy(false); return; }
          const { data: s } = await axios.get(`${API}/payments/direct-status/${data.payment_intent_id}`);
          if (s.status === "succeeded") { onPaid({ ...data, status: "succeeded" }); return; }
          setTimeout(() => poll(attempt + 1), 1500);
        };
        poll();
        return;
      }
      setError(`Payment status: ${data.status}. Please try again or contact us.`);
    } catch (e) {
      setError(e.response?.data?.detail || "Payment failed. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="card-payment-form">
      <div className="rounded-lg border border-white/15 bg-[#0A0A0A] p-3.5">
        <CardElement options={cardElementOptions} onChange={() => { setQuote(null); setError(""); }} />
      </div>
      {error && <p data-testid="card-payment-error" className="text-red-400 text-xs">{error}</p>}

      {!quote ? (
        <button data-testid="card-inspect-btn" onClick={inspectCard} disabled={busy || !stripe}
          className="w-full bg-white/10 hover:bg-white/15 text-white font-bold py-3 rounded-md transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
          {busy ? <Loader2 className="animate-spin" size={18} /> : <CreditCard size={18} />} Check Card &amp; Fee
        </button>
      ) : (
        <div className="space-y-3">
          <div data-testid="card-fee-exact-quote" className="rounded-lg border border-white/15 bg-white/[0.04] p-3 space-y-1">
            <div className="flex justify-between text-sm"><span className="text-white/45">Card Type</span><span className="text-white/85">{FUNDING_LABEL[funding] || "Unknown"}</span></div>
            <div className="flex justify-between text-sm"><span className="text-white/45">Subtotal</span><span className="text-white/85">{money(quote.subtotal)}</span></div>
            <div className="flex justify-between text-sm">
              <span className="text-white/45">Processing Fee</span>
              <span className="text-white/85" data-testid="card-fee-exact-amount">{quote.processing_fee > 0 ? money(quote.processing_fee) : "$0.00 — no fee"}</span>
            </div>
            <div className="flex items-center justify-between pt-1 border-t border-white/10">
              <span className="text-white/80 text-sm font-bold">Total Charge</span>
              <span data-testid="card-fee-exact-total" className="font-bold text-white">{money(quote.total_charged)}</span>
            </div>
            <p className="text-[10px] text-white/40 pt-1">{quote.disclosure}</p>
          </div>
          <button data-testid="card-confirm-btn" onClick={confirmPayment} disabled={busy}
            className="w-full bg-[#F2B705] text-black font-bold py-3.5 rounded-md hover:bg-white transition-colors flex items-center justify-center gap-2 disabled:opacity-60">
            {busy ? <Loader2 className="animate-spin" size={18} /> : <ShieldCheck size={18} />} Confirm {money(quote.total_charged)} Payment
          </button>
        </div>
      )}
    </div>
  );
}

export default function CardPaymentForm({ bookingId, balanceDue, onPaid }) {
  const stripePromise = useMemo(() => loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY), []);
  return (
    <Elements stripe={stripePromise}>
      <Inner bookingId={bookingId} balanceDue={balanceDue} onPaid={onPaid} />
    </Elements>
  );
}
